import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, ArrowRight, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import officeImage from "@assets/Page1_Office_1773659264352.webp";
import daveRogers from "@assets/Dave_Rogers_1773660481319.webp";
import archieK from "@assets/Archie_K_1773660481320.webp";
import ellieW from "@assets/Ellie_W_1773660481320.webp";
import chrisA from "@assets/Chris_A_1773660481320.webp";
import aleks from "@assets/Aleks_1773660481321.webp";
import catalinC from "@assets/Catalin_C_1773660481321.webp";
import samH from "@assets/Sam_H_1773660481322.webp";
import kayleyW from "@assets/Kayley_W_1773660481322.webp";
import dannyC from "@assets/Danny_C_1773660481323.webp";
import tomF from "@assets/Tom_F_1773660481323.webp";
import joF from "@assets/Jo_F_1773660481324.webp";
import justin from "@assets/Justin_1773660481324.webp";

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const team = [
    { name: "Dave", image: daveRogers },
    { name: "Aleks", image: archieK },
    { name: "Ellie", image: ellieW },
    { name: "Chris", image: chrisA },
    { name: "Archie", image: aleks },
    { name: "Catalin", image: catalinC },
    { name: "Sam", image: samH },
    { name: "Kayley", image: kayleyW },
    { name: "Danny", image: dannyC },
    { name: "Tom", image: tomF },
    { name: "Jo", image: joF },
    { name: "Justin", image: justin },
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>SEO Company in Cornwall | The Cornwall SEO Co.</title>
        <meta name="description" content="The Cornwall SEO Co. is part of an established SEO company, with clients throughout the UK. If you want to talk to real people, who really care, contact us." />
      </Helmet>
      <Navbar />

      <section className="relative overflow-hidden pt-28 pb-20 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-white/5" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <p className="text-primary font-black uppercase italic tracking-[0.35em] text-sm mb-5">About Us</p>
              <h1 className="text-5xl md:text-7xl font-heading font-black text-white mb-8 uppercase italic tracking-tighter">
                The Team <br /> Behind The Results
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl">
                We’re a Cornwall-based digital marketing team focused on practical, measurable results.
              </p>
              <div className="flex flex-col sm:flex-row gap-6">
                <Link href="/contact.html" className="inline-flex items-center justify-center bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all">
                    Start Your Journey <ArrowRight className="ml-2" />
                </Link>
              </div>
            </motion.div>
            <motion.img
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              src={officeImage}
              alt="The Cornwall SEO Co office"
              className="w-full rounded-none border-4 border-primary shadow-[16px_16px_0px_0px_rgba(255,255,255,0.15)]"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              "Straightforward communication",
              "Practical marketing strategy",
              "Personal support from a local team",
            ].map((item) => (
              <Card key={item} className="rounded-none border-2 border-black shadow-none">
                <CardContent className="p-6 flex items-start gap-4">
                  <CheckCircle2 className="text-primary shrink-0 mt-1" />
                  <p className="font-black uppercase italic text-black leading-snug">{item}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <Card className="rounded-none border-2 border-black shadow-none">
              <CardContent className="p-8 md:p-10 space-y-6 text-black leading-relaxed text-base md:text-lg">
                <p>The Cornwall SEO Co. is part of Page1 an established SEO company based in St Austell, Cornwall with clients throughout the UK and further afield.</p>
                <p>We have undertaken and passed the necessary exams to become a Google Partner and Bing Ads Accredited.</p>
                <p>Our philosophy is to keep things simple. We don't lock customers into long term contracts, charge them setup charges or make campaigns more complex than they need to be.</p>
                <p>We have individual specialists in SEO, Social Media, PPC, Outreach, Content and Web Development. Our rates are competitive and our clientele ranges from small business to public limited companies.</p>
                <p>We aim for 100% customer satisfaction from the start, focusing our attention on the specific goals of our clients. If you want to talk to real people who really care, contact us now for more information about our services.</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {team.map((member) => (
              <Card key={member.name} className="rounded-none border-2 border-black overflow-hidden shadow-none">
                <img src={member.image} alt={member.name} className="w-full aspect-square object-cover" />
                <CardContent className="p-4 bg-white">
                  <p className="font-black uppercase italic text-black text-sm tracking-widest">{member.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="bg-black p-10 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)]">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-4xl md:text-6xl font-heading font-black text-primary mb-6 uppercase italic tracking-tighter">
                Ready to Work <br/> Together?
              </h2>
              <p className="text-white/80 text-xl mb-12 font-medium">
                Get in touch with our team today. We'd love to discuss your digital marketing goals and how we can help you achieve them.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
                <Link href="/contact.html" className="inline-flex items-center justify-center bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all">
                    Start Your Journey <ArrowRight className="ml-2" />
                </Link>
              </div>
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                <a href="tel:0172674161" className="flex items-center gap-3 text-white hover:text-primary transition-colors">
                  <Phone size={24} className="text-primary" />
                  <span className="font-black uppercase text-lg">01726 74161</span>
                </a>
                <a href="mailto:hello@thecornwallseoco.co.uk" className="flex items-center gap-3 text-white hover:text-primary transition-colors">
                  <Mail size={24} className="text-primary" />
                  <span className="font-black uppercase text-sm md:text-lg break-words">hello@thecornwallseoco.co.uk</span>
                </a>
                <a href="https://wa.me/447792646061" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-primary transition-colors">
                  <MessageCircle size={24} className="text-primary" />
                  <span className="font-black uppercase text-lg hover:text-primary transition-colors">WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
